import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { StateBadge } from '@/components/platform/StateBadge';
import { InstitutionPicker } from '@/components/platform/InstitutionPicker';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { useAsyncData } from '@/hooks';
import { formSchemasApi } from '@/api/config-governance';
import {
  FORM_DOMAINS,
  LIFECYCLE_STATUSES,
  type FormDomain,
  type FormSchema,
} from '@/types/config-governance';
import styles from './platform-pages.module.css';
import gov from './governance.module.css';

/**
 * CFG-11 — the form schema register, grouped by domain.
 *
 * A schema version is listed under its domain because that is how the
 * question arrives — "what does the admission form ask?" — and within a
 * domain the code distinguishes variants (standard vs. boarding-school
 * admission). Platform templates and institution derivations share the list;
 * the scope tag and the base link on the detail page tell them apart.
 */

type ScopeChoice = 'all' | 'platform' | 'institution';

export function FormSchemasPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const status = params.get('status') ?? '';
  const scopeParam = params.get('scope') ?? '';
  const scope: ScopeChoice =
    scopeParam === 'platform' ? 'platform' : scopeParam ? 'institution' : 'all';
  const scopeInstitution = scope === 'institution' ? scopeParam.trim() : '';
  const [creating, setCreating] = useState(false);

  const fetcher = useCallback(() => {
    return formSchemasApi.list({
      ...(domain ? { formDomain: domain as FormDomain } : {}),
      ...(status ? { status } : {}),
      ...(scope === 'platform'
        ? { institutionId: 'platform' }
        : scope === 'institution' && scopeInstitution
          ? { institutionId: scopeInstitution }
          : {}),
    });
  }, [domain, status, scope, scopeInstitution]);

  const { state, reload } = useAsyncData<FormSchema[]>(
    `form-schemas:${domain}:${status}:${scopeParam}`,
    fetcher,
  );

  const setFilters = (nextDomain: string, nextStatus: string, nextScope: string) => {
    const next = new URLSearchParams();
    if (nextDomain) next.set('domain', nextDomain);
    if (nextStatus) next.set('status', nextStatus);
    if (nextScope) next.set('scope', nextScope);
    setParams(next);
  };

  const groups = useMemo(() => {
    if (state.status !== 'ready') return [];
    const byDomain = new Map<string, FormSchema[]>();
    for (const schema of state.data) {
      const list = byDomain.get(schema.formDomain) ?? [];
      list.push(schema);
      byDomain.set(schema.formDomain, list);
    }
    return FORM_DOMAINS.filter((d) => byDomain.has(d)).map(
      (d) => [d, byDomain.get(d) as FormSchema[]] as const,
    );
  }, [state]);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('formSchemas.title')}</h1>
          <p className={styles.pageLead}>{t('formSchemas.lead')}</p>
        </div>
        <button type="button" className={styles.primaryBtn} onClick={() => setCreating(true)}>
          <Icon name="plus" size={16} />
          {t('formSchemas.newSchema')}
        </button>
      </header>

      <div className={styles.filterBar} role="search">
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="schema-domain">
            {t('formSchemas.filterDomain')}
          </label>
          <Select
            id="schema-domain"
            value={domain}
            onChange={(next) => setFilters(next, status, scopeParam)}
            options={[
              { value: '', label: t('formSchemas.filterAllDomains') },
              ...FORM_DOMAINS.map((value) => ({
                value,
                label: t(`formSchemas.domains.${value}`, { defaultValue: value }),
              })),
            ]}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="schema-status">
            {t('acadPolicies.filterStatus')}
          </label>
          <Select
            id="schema-status"
            value={status}
            onChange={(next) => setFilters(domain, next, scopeParam)}
            options={[
              { value: '', label: t('grading.filterAllStatuses') },
              ...LIFECYCLE_STATUSES.map((value) => ({
                value,
                label: t(`platform.state.lifecycle.${value}`),
              })),
            ]}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="schema-scope">
            {t('grading.filterScope')}
          </label>
          <Select
            id="schema-scope"
            value={scope}
            onChange={(next) =>
              setFilters(domain, status, next === 'all' ? '' : next === 'platform' ? 'platform' : ' ')
            }
            options={[
              { value: 'all', label: t('grading.scopeAll') },
              { value: 'platform', label: t('formSchemas.scopePlatform') },
              { value: 'institution', label: t('grading.scopeInstitution') },
            ]}
          />
        </div>
        {scope === 'institution' && (
          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="schema-institution">
              {t('grading.institutionLabel')}
            </label>
            <InstitutionPicker
              id="schema-institution"
              value={scopeInstitution}
              onChange={(next) => setFilters(domain, status, next || ' ')}
            />
          </div>
        )}
      </div>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('formSchemas.emptyTitle')}
        emptyBody={t('formSchemas.emptyBody')}
      >
        {() => (
          <>
            {groups.map(([groupDomain, schemas]) => (
              <section key={groupDomain}>
                <h2 className={styles.subheading}>
                  {t(`formSchemas.domains.${groupDomain}`, { defaultValue: groupDomain })}
                </h2>
                <ul className={styles.rowList}>
                  {schemas.map((schema) => (
                    <li key={schema.id} className={styles.row}>
                      <div className={styles.rowMain}>
                        <Link
                          className={styles.rowTitleLink}
                          to={`/admin/form-schemas/${encodeURIComponent(schema.id)}`}
                        >
                          {schema.title}
                        </Link>
                        <div className={styles.rowMetaLine}>
                          <code className={styles.codeTag}>{schema.code}</code>
                          <span className={styles.mutedTag}>
                            {t('grading.versionShort', { n: schema.version })}
                          </span>
                          <StateBadge kind="lifecycle" value={schema.status} size="sm" />
                          <span className={gov.scopeTag}>
                            {schema.institutionId === null
                              ? t('formSchemas.scopePlatform')
                              : t('grading.scopeInstitution')}
                          </span>
                        </div>
                        {schema.description && (
                          <p className={styles.rowDetail}>{schema.description}</p>
                        )}
                      </div>
                      <div className={styles.rowAside}>
                        <span className={styles.mutedCell}>
                          {t('formSchemas.contentSummary', {
                            sections: schema.sections.length,
                            fields: schema.fields.length,
                          })}
                        </span>
                        <span className={styles.mutedCell}>
                          {t('formSchemas.protectedCount', {
                            total: schema.fields.filter((field) => field.protected).length,
                          })}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </>
        )}
      </AsyncPanel>

      <CreateSchemaDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setCreating(false);
          toast.success(t('formSchemas.createdToast', { code: created.code, n: created.version }));
          navigate(`/admin/form-schemas/${encodeURIComponent(created.id)}`);
        }}
      />
    </div>
  );
}

function CreateSchemaDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (created: FormSchema) => void;
}) {
  const { t } = useTranslation();
  const [formDomain, setFormDomain] = useState<FormDomain>('ADMISSION_APPLICATION');
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [institutionId, setInstitutionId] = useState('');
  const [baseSchemaId, setBaseSchemaId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Base templates only make sense for institution derivations, and only
  // platform templates of the SAME domain qualify (the server refuses the
  // rest with its own sentences) — so the picker is constrained up front.
  const baseFetcher = useCallback(
    () => formSchemasApi.list({ formDomain, institutionId: 'platform' }),
    [formDomain],
  );
  const bases = useAsyncData<FormSchema[]>(
    `form-schema-bases:${formDomain}:${open ? 'open' : 'closed'}`,
    baseFetcher,
    open && institutionId.length > 0,
  );

  const codeValid = /^[A-Z][A-Z0-9_]*$/.test(code);
  const canSubmit =
    codeValid &&
    title.trim().length > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom) &&
    !submitting;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Sections and fields start empty — unlike grading bands, the DTO
      // allows it, and the builder on the detail page is where the form takes
      // shape. Deriving from a base is different: the base's sections and
      // fields are copied so the protected floor arrives intact instead of
      // instantly violated.
      const base =
        baseSchemaId && bases.state.status === 'ready'
          ? bases.state.data.find((candidate) => candidate.id === baseSchemaId)
          : undefined;
      const created = await formSchemasApi.create({
        formDomain,
        code,
        title: title.trim(),
        effectiveFrom,
        sections: base ? base.sections : [],
        fields: base ? base.fields : [],
        ...(institutionId ? { institutionId } : {}),
        ...(institutionId && baseSchemaId ? { baseSchemaId } : {}),
      });
      onCreated(created);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('formSchemas.createTitle')}
      description={t('formSchemas.createIntro')}
      closeOnBackdropClick={false}
    >
      <div className={styles.dialogForm}>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-schema-domain">
            {t('formSchemas.fieldDomain')}
          </label>
          <Select
            id="new-schema-domain"
            value={formDomain}
            onChange={(next) => {
              setFormDomain(next as FormDomain);
              setBaseSchemaId('');
            }}
            options={FORM_DOMAINS.map((value) => ({
              value,
              label: t(`formSchemas.domains.${value}`, { defaultValue: value }),
            }))}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-schema-code">
            {t('formSchemas.fieldCode')}
          </label>
          <p className={styles.fieldHint}>{t('grading.fieldCodeHint')}</p>
          <input
            id="new-schema-code"
            className={gov.editorInput}
            value={code}
            spellCheck={false}
            aria-invalid={code.length > 0 && !codeValid}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-schema-title">
            {t('formSchemas.fieldTitle')}
          </label>
          <input
            id="new-schema-title"
            className={gov.editorInput}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-schema-effective">
            {t('formSchemas.fieldEffective')}
          </label>
          <input
            id="new-schema-effective"
            className={gov.editorInputNarrow}
            type="date"
            value={effectiveFrom}
            onChange={(event) => setEffectiveFrom(event.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-schema-institution">
            {t('grading.fieldScope')}
          </label>
          <p className={styles.fieldHint}>{t('formSchemas.fieldScopeHint')}</p>
          <InstitutionPicker
            id="new-schema-institution"
            value={institutionId}
            clearable
            onChange={(next) => {
              setInstitutionId(next);
              if (!next) setBaseSchemaId('');
            }}
            placeholder={t('formSchemas.scopePlatform')}
          />
        </div>
        {institutionId && (
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="new-schema-base">
              {t('formSchemas.fieldBase')}
            </label>
            <p className={styles.fieldHint}>{t('formSchemas.fieldBaseHint')}</p>
            <Select
              id="new-schema-base"
              value={baseSchemaId}
              onChange={setBaseSchemaId}
              options={[
                { value: '', label: t('formSchemas.noBase') },
                ...(bases.state.status === 'ready'
                  ? bases.state.data.map((candidate) => ({
                      value: candidate.id,
                      label: `${candidate.code} v${candidate.version}`,
                      description: candidate.title,
                    }))
                  : []),
              ]}
            />
          </div>
        )}

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button type="button" className="modal-button" disabled={!canSubmit} onClick={submit}>
            {submitting ? t('governance.transition.working') : t('formSchemas.createConfirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
